<script setup lang="ts">
import { IdvDocuments, UNKNOWN_DOCUMENT_TYPE, type IdvDocumentDisplay } from '@Constants/idvDocumentTypeDisplay.constants';
import { computed, onMounted, ref, useTemplateRef } from 'vue';
import { useGlobalStore } from '@Stores/global';
import  watchViewportInnerHeight from '@Utils/setViewport';
import { trackClientIdentityDocTypeSelected } from '@Analytics/auth.mixpanel';
import _ from 'lodash';
import { useRoute } from 'vue-router';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useIsMobile from '@Use/useIsMobile';
import InstructionsCarousel from './InstructionsCarousel.vue';
import { beginIdentityVerification, getCaptureOptions } from '@Api/idv.api';
import { storeMfeRoute } from '@Api/authFlow.api'
import { getCurrentMfeRoute } from 'host/mfeBridge'
import { SessionExpiredError, redirectToSessionExpired } from '@Utils/sessionExpired';

// import components
import { FlowLayout } from '@Shared';
import QrOptionForIdv from '@Components/QrOptionForIdv.vue';

// Stores/base data
const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const globalStore = useGlobalStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();

const { isMobile } = useIsMobile();

const docType = ref<IdvDocumentDisplay | undefined>(undefined);
const allDocTypes = computed(() => {
    return IdvDocuments;
});
const selectedDisplayText = computed(() => {return docType.value?.display || "Select"})
const hasDocType = computed(() => {return !!docType.value})

// template components
const carousel = useTemplateRef('carousel')
const showCarousels = ref(false);
const isLoading = ref(false);
const continueText = ref('Continue');

// Nothing renders until the capture options answer. The defaults are deliberately
// picker-on, so rendering first would flash a document question at a participant whose
// vendor never asks one.
const captureOptionsResolved = ref(false);
const vendorCollectsDocumentType = ref(false);

// The vendor runs its own picker, so this screen has nothing left to collect and performs
// the handoff itself. The route is kept rather than skipped: it still carries the
// Intellicheck picker and the desktop QR handoff.
const handsOffWithoutInput = computed(() => isMobile.value && vendorCollectsDocumentType.value);

// The self-handoff has no control of its own, so its loader is keyed on handsOffWithoutInput
// rather than on isLoading — which is permanently true once the vendor collects the type. A
// failed handoff therefore has to say so explicitly, or the loader covers the screen for the
// rest of the session with nothing left running behind it.
const handoffFailed = ref(false);

// mixpanel
const route = useRoute();
const context = _.isNil(route.query.context?.toString()) ? '' :route.query.context?.toString();

onMounted(async () => {
    globalStore.scrollToTop();
    watchViewportInnerHeight();

    showCarousels.value = true;

    ({ vendorCollectsDocumentType: vendorCollectsDocumentType.value } = await getCaptureOptions());
    captureOptionsResolved.value = true;

    if (handsOffWithoutInput.value) {
        await startCapture({ selfRedirect: true });
    }
})
const nextStep = async () => {
    if (!hasDocType.value) return;

    if(carousel.value && !carousel.value?.isOnLastSlide()) {
        carousel.value?.next();
        return;
    }

    trackClientIdentityDocTypeSelected(context, docType.value?.display!);

    await startCapture({ selfRedirect: false });
}

/**
 * Starts the capture and hands the participant to the vendor.
 *
 * `selfRedirect` distinguishes the two ways this screen leaves. On the self-handoff the URL is
 * replaced rather than pushed: keeping it in history would put it under the back button from the
 * vendor's own screen, where it would redirect forward again and trap the participant. The
 * document-picker path pushes, because the document they chose is a real place to return to.
 *
 * documentType is sent as Unknown when the vendor collects it — the capture app covers far more
 * types than the four this picker offers, so naming one here would narrow what the participant
 * may present. It is sent rather than omitted because StartCaptureFlow's IdvRequestDto declares
 * DocumentType `required`: an absent field is a 400 at deserialization, before the Socure path's
 * guard that exists precisely to let Unknown through (PLAT-3260).
 */
const startCapture = async ({ selfRedirect }: { selfRedirect: boolean }) => {
    isLoading.value = true;
    handoffFailed.value = false;

    try {
        await backupMfeRoute();

        const temporarySessionId = route.query.temporarySessionId?.toString();
        const clientId = route.query.clientId?.toString();
        const session = temporarySessionId ? { temporarySessionId } : { clientId: clientId! };
        const payload = vendorCollectsDocumentType.value
            ? { documentType: UNKNOWN_DOCUMENT_TYPE, ...session }
            : { documentType: docType.value?.value!, ...session };

        const idvResponse = await beginIdentityVerification(payload);

        if (selfRedirect) {
            window.location.replace(idvResponse.captureUrl);
        } else {
            window.location.href = idvResponse.captureUrl;
        }
    } catch (error) {
        if (error instanceof SessionExpiredError) {
            // Storing the return route found the session gone, so no capture can start from it.
            redirectToSessionExpired();
            return;
        }
        // Leaving the participant here with the screen live is the recoverable outcome. The
        // picker path still has its own control to retry from; the self-handoff has none, so it
        // raises handoffFailed to drop the loader and offer one.
        console.error('[Auth MFE] Failed to start the hosted capture:', error);
        isLoading.value = false;
        handoffFailed.value = true;
    }
}

const backupMfeRoute = async () => {
    const mfeRoute = getCurrentMfeRoute();
    if (mfeRoute){
        await storeMfeRoute(mfeRoute.fullPath);
    }
}

const setDocType = (doc: IdvDocumentDisplay) => {
    docType.value = doc
    setTimeout(() => {
        carousel.value?.reset();
    });
}

const onSlideChanged = () => {
    continueText.value = carousel.value?.isOnLastSlide()
    ? "Open Camera"
    : "I Understand";
}

</script>

<template>
    <!-- A brand loader covers both the pending capture-options call and the self-handoff that
         follows it, so neither the document question nor an Open Camera control is ever shown to
         a participant whose vendor collects the type itself. -->
    <app-page-loader v-if="isMobile && !handoffFailed && (!captureOptionsResolved || handsOffWithoutInput)" />

    <!-- The self-handoff could not start. Without this the loader above never comes down, because
         its condition holds for the whole session once the vendor collects the document type. -->
    <flow-layout
        v-else-if="isMobile && handoffFailed"
        suppress-back-to-dashboard
        :company="companyStore.company"
        :brand="companyStore.brand"
        :location="locationStore.location"
        :company-image-base-url="companyImageBaseUrl">
        <app-form-content-layout>
            <h1>We couldn't start the ID check</h1>
            <div>Something went wrong getting your camera ready. Please try again.</div>
        </app-form-content-layout>
        <action-bar-layout>
            <app-button
                expanded
                :is-loading="isLoading"
                size="large"
                type="primary"
                data-id="retry-identity"
                @onClick="startCapture({ selfRedirect: true })">
                Try Again
            </app-button>
        </action-bar-layout>
    </flow-layout>

    <template v-else-if="isMobile">
        <flow-layout v-if="allDocTypes"
            suppress-back-to-dashboard
            :company="companyStore.company"
            :brand="companyStore.brand"
            :location="locationStore.location"
            :company-image-base-url="companyImageBaseUrl">
            <app-form-content-layout>
                <h1>Choose your ID type</h1>
                <div>
                    Choose the identification document that you're using for this identity verification.
                </div>
                <app-menu
                v-if="selectedDisplayText"
                    branded
                    expanded
                    data-testid="doc-type-selector">
                    <template #trigger>
                        <app-input-select-trigger
                        size="large"
                        expanded>
                        {{ selectedDisplayText }}
                    </app-input-select-trigger>
                    </template>
                    <app-menu-item 
                        @onClick="setDocType(allDocTypes.NorthAmericanDriversLicense)">
                        Driver's License or ID Card
                        <template #description>
                            <div>
                            Real ID, Enhanced ID, Learner's permit, Military ID, Non-driver state ID
                            </div>
                        </template>
                        <template #subtext>
                            <div>US, Canada, and Mexico only</div>
                        </template>
                    </app-menu-item>
                    <app-menu-item 
                        @onClick="setDocType(allDocTypes.Passport)">
                        Passport
                        <template #description>
                            <div>Passport booklet, Visa</div>
                        </template>
                        <template #subtext>
                            <div>Any country</div>
                        </template>
                    </app-menu-item>
                    <app-menu-item 
                        @onClick="setDocType(allDocTypes.UsPassportCard)">
                        US Passport Card
                        <template #description>
                            <div>US Passport Card (wallet-size card, not the passport booklet)</div>
                        </template>
                        <template #subtext>
                            <div>US only</div>
                        </template>
                    </app-menu-item>
                    <app-menu-item 
                        @onClick="setDocType(allDocTypes.Other)">
                        Other
                        <template #description>
                        <div>
                            International passport card, trusted traveler card, permanent resident card
                        </div>
                        </template>
                        <template #subtext>
                        <div>Any country</div>
                        </template>
                    </app-menu-item>
                </app-menu>
            
                <div v-if="hasDocType"
                    class="continue-container"
                    :class="{ 'instructions-hidden': !hasDocType, 'instructions-active' : hasDocType }">
                    <InstructionsCarousel
                        ref="carousel"
                        class="doc-type-carousel"
                        :doc-type="docType"
                        @slide-changed="onSlideChanged" />
                    <app-button
                        expanded
                        :is-loading="isLoading"
                        size="large"
                        type="primary"
                        data-id="continue-identity"
                        @onClick="nextStep">
                        {{ continueText }}
                    </app-button>
                </div>

            </app-form-content-layout>

        </flow-layout>
    </template>
    <template v-else>
        <qr-option-for-idv />
    </template>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.doc-type-carousel {
    margin-top: 1rem;
    width: 100%;
}

.app-button {
    margin-top: 2rem;
}

.continue-container {
    position: relative;
    width: 100%;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;

    &.instructions-hidden {
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.8s ease, visibility 0.8s ease;
    }

    &.instructions-active {
        opacity: 1;
        visibility: visible;
        transition: opacity 0.8s ease, visibility 0.8s ease;
    }
}
</style>